import React, { useState, useEffect } from 'react';
import { Cookie, X } from 'lucide-react';
import { Button } from './ui/button';

const CookieBanner = ({ language }) => {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    // Check if user has already acknowledged the cookie banner
    const cookieConsent = localStorage.getItem('cookieConsent');
    if (!cookieConsent) {
      // Small delay for better UX
      const timer = setTimeout(() => setIsVisible(true), 1000);
      return () => clearTimeout(timer);
    }
  }, []);

  const handleAccept = () => {
    localStorage.setItem('cookieConsent', 'accepted');
    setIsVisible(false);
  };

  const translations = {
    de: {
      title: 'Hinweis zu Cookies',
      message: 'Diese Website verwendet keine Tracking-Cookies. Es werden lediglich Ihre Einstellungen (Sprache, Dark Mode) lokal in Ihrem Browser gespeichert, um Ihnen ein besseres Nutzererlebnis zu bieten.',
      moreInfo: 'Mehr Informationen finden Sie in meiner',
      privacyLink: 'Datenschutzerklärung',
      accept: 'Verstanden'
    },
    en: {
      title: 'Cookie Notice',
      message: 'This website does not use tracking cookies. Only your settings (language, dark mode) are stored locally in your browser to provide you with a better user experience.',
      moreInfo: 'For more information, please see my',
      privacyLink: 'Privacy Policy',
      accept: 'Got it'
    }
  };

  const t = translations[language] || translations.de;

  if (!isVisible) return null;

  return (
    <div 
      className="fixed bottom-0 left-0 right-0 z-[9998] p-4 animate-in slide-in-from-bottom duration-500"
      data-testid="cookie-banner"
    >
      <div className="container mx-auto max-w-4xl">
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-2xl border border-gray-200 dark:border-gray-700 p-4 sm:p-6">
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
            {/* Icon */}
            <div className="flex-shrink-0 hidden sm:flex w-12 h-12 rounded-full bg-[#E53D4A]/10 items-center justify-center">
              <Cookie className="h-6 w-6 text-[#E53D4A]" />
            </div>
            
            {/* Content */}
            <div className="flex-grow">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-1 flex items-center gap-2">
                <Cookie className="h-5 w-5 text-[#E53D4A] sm:hidden" />
                {t.title}
              </h3>
              <p className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed">
                {t.message}{' '}
                <span className="inline">
                  {t.moreInfo}{' '}
                  <a 
                    href="/privacy" 
                    className="text-[#E53D4A] hover:underline font-medium"
                  >
                    {t.privacyLink}
                  </a>.
                </span>
              </p>
            </div>
            
            {/* Actions */}
            <div className="flex items-center gap-2 w-full sm:w-auto">
              <Button
                onClick={handleAccept}
                className="flex-1 sm:flex-none bg-[#E53D4A] hover:bg-[#d63340] text-white px-6"
                data-testid="cookie-accept-btn"
              >
                {t.accept}
              </Button>
              <button
                onClick={handleAccept}
                className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 transition-colors sm:hidden"
                aria-label="Close"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CookieBanner;
